import React, { useEffect, useState, useContext } from 'react';
import _ from 'lodash';
import { ButtonColor, ButtonVariant, Flex, FlexBehavior, FlexJustify, Spinner } from 'react-magma-dom';
import uuid from 'uuid-random';
import { exemplarApi } from '../../../API/exemplar';
import { CloseModalIcon } from '../../../assets/icons/ImageEditTool';
import { AWSImage, ImageDimensions, ImageSide, WritingType } from '../../../types';
import {
  sortExemplarImagesByCurrentUUID,
  sortProformaImagesByCurrentUUID,
  spliceImageToStart,
} from '../../../utils/images';
import { StyledCloseImagesModalIconButton } from '../../Styled/StyledButtons';
import { DEFAULT_EXEMPLAR_HEIGHT, DEFAULT_EXEMPLAR_WIDTH, DEFAULT_IMAGE_DIMENSIONS } from '../../../constants/exemplar';
import { ReplaceImageErrorContent } from './ReplaceImageErrorContent';
import { createPortal } from 'react-dom';
import { ModalWindowDefaultImages } from '../../Styled/StyledModal';
import { ReplaceImageModalContent } from './ReplaceImageContent';
import { ImageContext } from '../../../contexts/imageContext';
import { ExemplarContext as LaterContext } from '../../../contexts/laterContext';
import { ExemplarContext as EmergentContext } from '../../../contexts/emergentContext';
import { useParams } from '@reach/router';
import { EMERGENT } from '../../../constants';
import { WORDS } from '../../../constants/navigation';

interface IReplaceImageModal {
  default_uuid: string | undefined;
  exemplarId: number;
  imageAccessKey?: string;
  currentImageUUID: string;
  defaultImageDimensions?: ImageDimensions;
  mode?: string;
  side?: ImageSide;
  sectionId: string;
  relatedImages: Array<AWSImage>;
  writingType: WritingType;
  color: string;
  setRelationships: (defaultImages: AWSImage[]) => void;
  replaceImage: (
    imageUUIDtoReplace: string,
    uuid: string,
    default_uuid: string | undefined,
    defaultImageDimensions: ImageDimensions,
    selectedImageDimensions: ImageDimensions,
    sectionId: string,
    side?: ImageSide
  ) => void;
  onClose: () => void;
}

export const ReplaceImageModal: React.FC<IReplaceImageModal> = ({
  default_uuid,
  currentImageUUID,
  side,
  sectionId,
  relatedImages,
  defaultImageDimensions,
  writingType,
  color,
  setRelationships,
  replaceImage,
  onClose,
}): JSX.Element => {
  const isExemplar = writingType === WritingType.exemplars;

  if (!isExemplar) {
    defaultImageDimensions = DEFAULT_IMAGE_DIMENSIONS;
  }

  const imageUUIDtoReplace = currentImageUUID;

  const [selectedImageUUID, selectImageUUID] = useState(currentImageUUID);
  const [selectedImageDimensions, setSelectedImageDimensions] = useState<ImageDimensions>(DEFAULT_IMAGE_DIMENSIONS);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { stage, page } = useParams();
  const { state, setDefaultImages } = useContext(ImageContext);
  const { isImagesUploaded, defaultImages } = state;
  const [data, setData] = useState<AWSImage[]>([]);

  const { saveLaterImageUpdates } = useContext(LaterContext);
  const { saveEmergentImageUpdates } = useContext(EmergentContext);
  const moveCurrentImageToFirst = (imagesToShow: AWSImage[]) => {
    isExemplar
      ? sortExemplarImagesByCurrentUUID(currentImageUUID, imagesToShow, relatedImages)
      : sortProformaImagesByCurrentUUID(currentImageUUID, imagesToShow);
  };

  const moveDefaultImageToFirst = (default_uuid: string, imagesToShow: AWSImage[]) => {
    const image = imagesToShow.find(({ uuid }) => uuid === default_uuid);
    if (image === undefined) {
      spliceImageToStart(default_uuid, imagesToShow, relatedImages);
    }

    moveCurrentImageToFirst(imagesToShow);
  };

  const setRelationshipsAndMoveImageToFirst = (images: AWSImage[]): void => {
    setRelationships(images);
    !default_uuid || default_uuid === currentImageUUID
      ? moveCurrentImageToFirst(images)
      : moveDefaultImageToFirst(default_uuid, images);
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    (async () => {
      try {
        if (!isImagesUploaded) {
          const {
            data: { data },
          } = await exemplarApi.getImages();
          const imagesFromResponse = _.clone(data);
          setDefaultImages(imagesFromResponse);

          setRelationshipsAndMoveImageToFirst(data);
          setData(data.filter(image => image.url));
          setLoading(true);
          return;
        }

        const images = _.clone(defaultImages);
        setRelationshipsAndMoveImageToFirst(images);
        setData(images);
      } catch (error) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleReplaceButtonClick = (uuid: string) => {
    if (uuid === imageUUIDtoReplace) {
      onClose();
    } else {
      onClose();

      if (!defaultImageDimensions) return;

      const defaultUUID = default_uuid === undefined ? imageUUIDtoReplace : default_uuid;

      const state = {
        imageUUIDtoReplace,
        defaultImageDimensions,
        selectedImageDimensions,
        sectionId,
        side,
        uuid: selectedImageUUID,
        default_uuid: defaultUUID,
      };
      const prevState = {
        ...state,
        imageUUIDtoReplace: selectedImageUUID,
        uuid: imageUUIDtoReplace,
      };

      if (isExemplar && page != WORDS) {
        stage === EMERGENT ? saveEmergentImageUpdates(prevState, state) : saveLaterImageUpdates(prevState, state);
      }

      replaceImage(
        imageUUIDtoReplace,
        selectedImageUUID,
        default_uuid,
        defaultImageDimensions,
        selectedImageDimensions,
        sectionId,
        side
      );
    }
  };

  const onImageSelect = (event: React.MouseEvent<HTMLImageElement>, imageId: string) => {
    selectImageUUID(imageId);

    const image = event.target as HTMLImageElement;

    setSelectedImageDimensions({
      width: image.naturalWidth,
      height: image.naturalHeight,
      exemplarWidth: DEFAULT_EXEMPLAR_WIDTH,
      exemplarHeight: DEFAULT_EXEMPLAR_HEIGHT,
    });
  };

  const buttonText = (): string => {
    if (isExemplar) {
      return 'Replace';
    }

    return !uuid.test(currentImageUUID) ? 'Add' : 'Replace';
  };

  const isButtonDisabled = !data.find(image => image.uuid === selectedImageUUID);

  const renderContent = (): JSX.Element => {
    if (isLoading) {
      return (
        <Flex behavior={FlexBehavior.container} justify={FlexJustify.center}>
          <Spinner color={color} size={50} />
        </Flex>
      );
    }

    if (error) {
      return <ReplaceImageErrorContent color={color} />;
    }

    return (
      <ReplaceImageModalContent
        defaultImages={data}
        relatedImages={relatedImages}
        selectedImageUUID={selectedImageUUID}
        color={color}
        isButtonDisabled={isButtonDisabled}
        buttonText={buttonText()}
        handleReplaceButtonClick={handleReplaceButtonClick}
        onImageSelect={onImageSelect}
      />
    );
  };

  return createPortal(
    <ModalWindowDefaultImages isOpen isCloseButtonHidden onClose={onClose}>
      <StyledCloseImagesModalIconButton
        aria-label="Close default images modal window"
        color={ButtonColor.secondary}
        variant={ButtonVariant.link}
        icon={<CloseModalIcon />}
        onClick={onClose}
      />
      {renderContent()}
    </ModalWindowDefaultImages>,
    document.getElementsByTagName('body')[0] as HTMLElement
  );
};
